import React from "react";
import { Text } from "@shopify/polaris";

const StartGuideVideo = ({ src, title = "video" }) => (
  <div className="StartGuideStep__video">
    <div className="StartGuideStep__videoFrame">
      <iframe src={src} allowFullScreen title={title} />
    </div>
  </div>
);

const renderBulletContent = (bullet) => {
  if (
    bullet &&
    typeof bullet === "object" &&
    !React.isValidElement(bullet) &&
    ("label" in bullet || "text" in bullet)
  ) {
    return (
      <>
        {bullet.label ? <b>{bullet.label}</b> : null}
        {bullet.label && bullet.text ? " — " : null}
        {bullet.text}
      </>
    );
  }

  return bullet;
};

const StartGuideBullets = ({ items }) => {
  if (!items || items.length === 0) return null;

  return (
    <ul className="StartGuideStep__bullets">
      {items.map((bullet, index) => (
        <li key={index}>{renderBulletContent(bullet)}</li>
      ))}
    </ul>
  );
};

const StartGuideNote = ({ note }) => {
  if (!note) return null;

  return (
    <div className="StartGuideStep__noteBox">
      {note.title && (
        <Text as="p" color="subdued">
          <b>{note.title}</b>
        </Text>
      )}

      {note.items && note.items.length > 0 && (
        <ul>
          {note.items.map((item, index) => (
            <li key={index}>{renderBulletContent(item)}</li>
          ))}
        </ul>
      )}

      {note.footer && (
        <div className="StartGuideStep__noteFooter">
          <Text as="p" color="subdued">
            {note.footer}
          </Text>
        </div>
      )}
    </div>
  );
};

const StartGuideDocLink = ({ docLink }) => {
  if (!docLink || !docLink.href) return null;

  return (
    <Text as="p" color="subdued">
      {docLink.text ? `${docLink.text} ` : null}
      <a
        href={docLink.href}
        target="_blank"
        rel="noreferrer"
        className="StartGuideStep__docLink"
      >
        {docLink.label || docLink.href}
      </a>
    </Text>
  );
};

export default function StartGuideStep({
  content = {},
  complete = false,
  onAction,
}) {
  if (typeof content.render === "function") {
    return content.render({ complete, onAction });
  }

  const {
    videoUrl,
    title,
    description,
    bullets,
    note,
    sections = [],
    docLink,
    actions = [],
  } = content;

  return (
    <div className="StartGuideStep">
      {videoUrl && <StartGuideVideo src={videoUrl} title={title} />}

      <div className="StartGuideStep__copy">
        {title && (
          <Text as="p" fontWeight="bold">
            {title}
          </Text>
        )}

        {description && (
          <Text as="p" color="subdued">
            {description}
          </Text>
        )}

        <StartGuideBullets items={bullets} />
        <StartGuideNote note={note} />

        {sections.map((section, index) => (
          <React.Fragment key={index}>
            {section.title && (
              <Text as="p" color="subdued">
                {section.title}
              </Text>
            )}
            <StartGuideBullets items={section.bullets} />
            <StartGuideNote note={section.note} />
          </React.Fragment>
        ))}

        <StartGuideDocLink docLink={docLink} />
      </div>

      {!complete && actions.length > 0 && (
        <div className="StartGuideStep__actions">
          {actions.map((action, index) => (
            <button
              key={action.id || index}
              type="button"
              className="StartGuideStep__link"
              onClick={(event) => onAction?.(action, event)}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
